import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, FileClock } from 'lucide-react';
import type { ClinicalFormCode } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { dateOnly, dateTime } from '@/lib/utils';
import { Alert, Badge, Button, DatePicker, Field, Input, Select, Switch } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { FORM_FIELDS, formFieldLabel, formMissingRequired, formOptionLabel, formPayload, type FormFieldSpec } from '../clinical-form-fields';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { EmrEmpty, EmrPanel } from './EmrPanel';
import { clinicalFormKeysToRefresh, clinicalFormsKey, clinicalFormsPath, type ClinicalFormScope } from './clinical-form-scope';

interface FormRow {
  id: string;
  formCode: string;
  data: Record<string, unknown>;
  isAddendum: boolean;
  recordedAt: string;
}

const BUTTONS_UP_TO = 5;

function displayValue(spec: FormFieldSpec | undefined, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return tr('opd', value ? 'yes' : 'no');
  if (Array.isArray(value)) {
    return value
      .map((item) => (item && typeof item === 'object' && 'vaccine' in item ? String((item as { vaccine: unknown }).vaccine) : String(item)))
      .join(', ');
  }
  if (spec?.type === 'select') return formOptionLabel(String(value));
  if (spec?.type === 'date') return dateOnly(String(value));
  return String(value);
}

function FormFieldInput({ spec, value, onChange }: { spec: FormFieldSpec; value: string | boolean | undefined; onChange: (next: string | boolean) => void }) {
  const label = formFieldLabel(spec);
  if (spec.type === 'boolean') {
    return <Switch checked={value === true} onCheckedChange={(checked) => onChange(checked)} label={label} className="self-end pb-2" />;
  }
  if (spec.type === 'select' && (spec.options?.length ?? 0) <= BUTTONS_UP_TO) {
    return (
      <Field label={label} required={spec.required} className="sm:col-span-2">
        <ChoiceButtons
          inline
          value={typeof value === 'string' && value ? value : null}
          onClear={spec.required ? undefined : () => onChange('')}
          options={(spec.options ?? []).map((option) => ({ value: option, label: formOptionLabel(option) }))}
          onChange={(next) => onChange(next)}
        />
      </Field>
    );
  }
  if (spec.type === 'select') {
    return (
      <Field label={label} required={spec.required}>
        <Select value={String(value ?? '')} onChange={(event) => onChange(event.target.value)}>
          <option value="">{tr('opd', 'choose')}</option>
          {spec.options?.map((option) => (
            <option key={option} value={option}>
              {formOptionLabel(option)}
            </option>
          ))}
        </Select>
      </Field>
    );
  }
  if (spec.type === 'date') {
    return (
      <Field label={label} required={spec.required}>
        <DatePicker value={String(value ?? '')} onChange={(next) => onChange(next)} />
      </Field>
    );
  }
  return (
    <Field label={label} required={spec.required} hint={spec.type === 'list' ? tr('opd', 'separate_items_with_commas') : undefined}>
      <Input
        type={spec.type === 'number' ? 'number' : 'text'}
        inputMode={spec.type === 'number' ? 'decimal' : undefined}
        value={String(value ?? '')}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}

export function ClinicalFormSection({ scope, code, closed, canWrite }: { scope: ClinicalFormScope; code: ClinicalFormCode; closed: boolean; canWrite: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { hasModule } = useAuth();
  const specs = FORM_FIELDS[code] ?? [];
  const title = tr('opd', `emr_form_${code}`);
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const forms = useQuery({ queryKey: clinicalFormsKey(scope), queryFn: () => get<FormRow[]>(clinicalFormsPath(scope)) });
  const entries = (forms.data ?? []).filter((row) => row.formCode === code).reverse();
  const inMaternity = code === 'obstetric_history' && hasModule('maternity');

  const save = useMutation({
    mutationFn: () => post(clinicalFormsPath(scope), { formCode: code, data: formPayload(specs, values) }),
    onSuccess: () => {
      toast.success(tr('opd', 'form_saved'));
      setValues({});
      for (const queryKey of clinicalFormKeysToRefresh(scope)) void queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => toast.error(tr('opd', 'could_not_save_the_form'), describeError(error)),
  });

  const writable = canWrite && !inMaternity;
  const incomplete = formMissingRequired(specs, values);

  return (
    <div className="space-y-4">
      <EmrPanel
        icon={ClipboardList}
        title={title}
        description={closed ? tr('opd', 'admissionId' in scope ? 'emr_form_saved_as_addendum_stay' : 'emr_form_saved_as_addendum') : tr('opd', 'emr_desc_clinical_form')}
        actions={
          writable && (
            <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={incomplete}>
              {tr('opd', 'save')}
            </Button>
          )
        }
      >
        {inMaternity ? (
          <Alert>{tr('opd', 'emr_obstetric_in_maternity')}</Alert>
        ) : writable ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {specs.map((spec) => (
              <FormFieldInput key={spec.key} spec={spec} value={values[spec.key]} onChange={(next) => setValues((current) => ({ ...current, [spec.key]: next }))} />
            ))}
          </div>
        ) : (
          <EmrEmpty>{tr('opd', 'emr_form_read_only')}</EmrEmpty>
        )}
      </EmrPanel>

      <EmrPanel icon={FileClock} title={tr('opd', 'admissionId' in scope ? 'emr_form_recorded_stay' : 'emr_form_recorded')} bodyClassName="space-y-3">
        {entries.length === 0 && <EmrEmpty>{tr('opd', 'emr_form_none_recorded')}</EmrEmpty>}
        {entries.map((row) => (
          <article key={row.id} className="rounded-xl border p-3">
            <header className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{dateTime(row.recordedAt)}</span>
              {row.isAddendum && <Badge variant="secondary">{tr('opd', 'emr_addendum_badge')}</Badge>}
            </header>
            <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
              {Object.entries(row.data).map(([key, value]) => {
                const spec = specs.find((candidate) => candidate.key === key);
                return (
                  <div key={key} className="min-w-0">
                    <dt className="text-[11px] font-medium text-muted-foreground">{spec ? formFieldLabel(spec) : key}</dt>
                    <dd className="break-words">{displayValue(spec, value)}</dd>
                  </div>
                );
              })}
            </dl>
          </article>
        ))}
      </EmrPanel>
    </div>
  );
}
